import React from 'react';
import Bootstrap from 'src/components/pages/_bootstrap/bootstrap';
import { BackgroundRecolorRedirect } from 'src/components/pages/backgroundColor/redirect';
import { ECommerceRedirect } from 'src/components/pages/ecommerceConnection/redirect';
import PaletteColorsPage from 'src/components/pages/settings/colorPaletteSettings/paletteColorsPage';
import PantoneLibraryPage from 'src/components/pages/settings/colorPaletteSettings/pantoneLibrary/pantoneLibraryPage';
import { backofficeUrl } from './helpers';

const Dashboard = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "dashboard" */ 'src/components/pages/dashboard'));
const PlatformSelection = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "platformSelection" */ 'src/components/pages/platformSelection'));

const EcommerceStart = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "ecommerceStart" */ 'src/components/pages/ecommerceStart'));
const ChoosePlan = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "choosePlan" */ 'src/components/pages/choosePlan'));
const ChoosePlanV2 = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "choosePlan" */ 'src/components/pages/choosePlan/choosePlanV2'));
const SetDefaultPlan = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "setDefaultPlan" */ 'src/components/pages/choosePlan/setDefaultPlan')
);
const StartsShopifySubscription = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "startsShopifySubscription" */ 'src/components/pages/choosePlan/startsShopifySubscription')
);
const ProductConfiguration = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "productConfiguration" */ 'src/components/pages/productConfiguration')
);
const TestPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "testPage" */ 'src/components/pages/testPage'));
const OfflineOrder = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "offlineOrder" */ 'src/components/pages/offlineOrder'));
const CreateOfflineOrder = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "createOfflineOrder" */ 'src/components/pages/createOfflineOrder')
);

const PricingsType = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "pricingsType" */ 'src/components/pages/pricingsType'));
const PricingsAdvanced = React.lazy(
    () => import(/* webpackChunkName: "pricingsAdvanced", webpackPrefetch: true */ 'src/components/pages/pricingsAdvanced/pricingsConfiguration')
);
const PricingsBase = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "pricingsBase" */ 'src/components/pages/pricingsBase'));
const Pricing = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "pricings" */ 'src/components/pages/pricingsAdvanced/pricing'));

const PrintTypePage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "printType" */ 'src/components/pages/printType'));
const PrintTypes = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "printTypes" */ 'src/components/pages/printTypes'));

const AdditionalAttributes = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "additionalAttributes" */ 'src/components/pages/additionalAttributes')
);
const AdditionalAttributePage = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "additionalAttribute" */ 'src/components/pages/additionalAttributes/additionalAttribute')
);

const Orders = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "orders" */ 'src/components/pages/orders'));
const OrdersSettings = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "ordersSettings" */ 'src/components/pages/ordersSettings'));
const OrderDesignEditor = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "order" */ 'src/components/pages/orderDesignEditor'));

const ShopifyOrderDetailContent = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "shopifyOrderDetailContent" */ 'src/components/pages/orders/shopifyOrderDetailContent')
);
const MarketplaceOrderPage = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "marketplaceOrderPage" */ 'src/components/pages/orders/marketplace/marketplaceOrder/marketplaceOrderPage'
        )
);
const OrderPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "orderPage" */ 'src/components/pages/order/orderPage'));

const MarketplaceOrder = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "marketplaceOrder" */ 'src/components/pages/orders/marketplace/marketplaceOrder/marketplaceOrderPage'
        )
);

const BulkProductsList = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "bulkProductsList" */ 'src/components/pages/bulkProductsList'));
const BulkVariationPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "bulkVariation" */ 'src/components/pages/bulkVariation'));

const UserCheckout = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "checkout" */ 'src/components/pages/checkout'));

const UserPage = React.lazy(() => import('src/components/pages/myAccount'));

const Support = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "support" */ 'src/components/pages/support/support'));

const CustomPrintFileNames = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customPrintFileNames" */ 'src/components/pages/customPrintFileNames')
);

const ShopifyNeedUpdateScope = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "ShopifyNeedUpdateScope" */ 'src/components/pages/settings/shopifyNeedUpdateScope')
);

const PlatformIntegrationSettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "platformIntegrationSettings" */ 'src/components/pages/platformIntegrationSettings')
);
const GeneralSettings = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "genericSettings" */ 'src/components/pages/generalSettings'));
const GeneralSettingsConfigurator = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "generalSettingsConfigurator" */ 'src/components/pages/generalSettingsConfigurator')
);

const ExtensionFieldsSettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "extensionFields" */ 'src/components/pages/settings/extensionFields')
);
const ExtensionFieldPage = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "extensionFieldPage" */ 'src/components/pages/settings/extensionFieldPage')
);

const Shapes = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "shapes" */ 'src/components/pages/shapes'));

const Images = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "images" */ 'src/components/pages/images'));

const InterfaceEditor = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "interfaceEditor" */ 'src/components/pages/interfaceEditor'));

const UserInterfaceNew = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "userInterfaceNew" */ 'src/components/pages/userInterfaceSettings/userInterfaceSettingsEditor')
);
const UserInterfaceSettingsEditor = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "userInterfaceSettingsEditor" */ 'src/components/pages/userInterfaceSettings/userInterfaceSettingsEditor'
        )
);
const UserInterfaceSettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "userInterfaceSettings" */ 'src/components/pages/userInterfaceSettings')
);
const UserInterfaceSettingsConfigurator = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "userInterfaceSettingsConfigurator" */ 'src/components/pages/userInterfaceSettingsConfigurator')
);
const CustomizerThemeItems = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customizerThemeItems" */ 'src/components/pages/settings/customizerThemeItems')
);
const CustomizerThemeItemPage = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customizerThemeItem" */ 'src/components/pages/settings/customizerThemeItem')
);

const Translations = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "translations" */ 'src/components/pages/settings/translations'));

const Hotjar = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "hotjar" */ 'src/components/pages/integrationSettings/hotjar'));
const GoogleTagManager = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "googleTagManager" */ 'src/components/pages/integrationSettings/googleTagManager')
);
const Zapier = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "zapier" */ 'src/components/pages/integrationSettings/zapier'));
const WebHooksSettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "webHooksSettings" */ 'src/components/pages/integrationSettings/webHooksSettings/webHooksSettings')
);

const CliLogin = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "cliLogin" */ 'src/components/pages/cliLogin'));

const Fonts = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "fonts" */ 'src/components/pages/tools/fonts'));
const CollageLayout = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "collageLayout" */ 'src/components/pages/tools/collageLayout/collageLayout')
);
const GridRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "gridRules" */ 'src/components/pages/tools/collageLayout/gridRules/gridRules')
);
const GridRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "gridRule" */ 'src/components/pages/tools/collageLayout/gridRules/gridRule')
);
const GetAQuote = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "getAQuote" */ 'src/components/pages/tools/getAQuote'));
const QuoteRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "quoteRules" */ 'src/components/pages/tools/getAQuote/rules/quoteRules')
);
const QuoteRule = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "quoteRule" */ 'src/components/pages/tools/getAQuote/rules/quoteRule'));
const Quotes = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "quotes" */ 'src/components/pages/tools/getAQuote/quotes/quotes'));
const Quote = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "quote" */ 'src/components/pages/tools/getAQuote/quotes/quote'));
const QuoteNotificationSettings = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "quoteNotificationSettings" */ 'src/components/pages/tools/getAQuote/quoteNotificationSettings/quoteNotificationSettings'
        )
);

const SettingsMessages = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "printTypes" */ 'src/components/pages/settings/customizerMessages')
);
const SettingsMessage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "printTypes" */ 'src/components/pages/settings/customizerMessage'));
const CustomizerMessagesByEvent = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customizerMessagesByEvent" */ 'src/components/pages/settings/customizerMessagesByEvent')
);

const CustomizerMessageByEvent = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customizerMessageByEvent" */ 'src/components/pages/settings/customizerMessageByEvent')
);

const DesignToTemplate = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "designToTemplate" */ 'src/components/pages/designToTemplate'));

const ProductTemplates = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "template-product-selection" */ 'src/components/pages/productTemplates')
);
const ProductItemTemplates = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "template-product-selection-templates" */ 'src/components/pages/productTemplates/productItemTemplates'
        )
);
const TemplateTaskDetail = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "template-task-detail" */ 'src/components/pages/productTemplates/templateTaskDetail')
);

const ColorSystem = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "designToTemplate" */ 'src/components/pages/colorSystem'));
const ColorSystemPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "designToTemplate" */ 'src/components/pages/colorSystemPage'));

// Landings
const CustomizerLanding = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customizerLanding" */ 'src/components/pages/landings/customizerLanding')
);
const ConfiguratorLanding = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "configuratorLanding" */ 'src/components/pages/landings/configuratorLanding')
);
const ViewerLanding = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "viewerLanding" */ 'src/components/pages/landings/viewerLanding'));
const PdfPreviewSettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "pdfPreviewSettings" */ 'src/components/pages/pdfPreviewSettings/pdfPreviewSettings')
);
const PdfSummarySettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "pdfSummarySettings" */ 'src/components/pages/pdfSummarySettings/pdfSummarySettings')
);
const QuoteSummarySettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "pdfSummarySettings" */ 'src/components/pages/quoteSummarySettings/quoteSummarySettings')
);

const ColorPaletteSettings = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "ColorPaletteSettings" */ 'src/components/pages/settings/colorPaletteSettings/index')
);
// const PaletteColorsPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "paletteColors" */ 'src/components/pages/settings/PaletteColors/index'));

// const PantoneLibraryPage = React.lazy(
// 	() => import(/* webpackPrefetch: true, webpackChunkName: "pantoneLibrary" */ 'src/components/pages/settings/colorLibrarySettings/pantoneLibrary/pantoneLibraryPage')
// );

const StandaloneSites = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "standaloneSites" */ 'src/components/pages/settings/standaloneSites')
);
const StandaloneSiteDetail = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "standaloneSiteDetail" */ 'src/components/pages/settings/standaloneSites/standaloneSiteDetail')
);
const StandaloneSiteCollectionDetail = React.lazy(
    () =>
        import(/* webpackPrefetch: true, webpackChunkName: "standaloneSiteCollectionDetail" */ 'src/components/pages/settings/standaloneSites/collectionDetail')
);
const ThemeSettingsEditor = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "themeSettingsEditor" */ 'src/components/pages/settings/standaloneSites/themeSettings')
);

const SalesChannels = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "salesChannels" */ 'src/components/pages/salesChannels'));
const SalesChannelSwitchTasks = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "salesChannelSwitchTasks" */ 'src/components/pages/salesChannels/salesChannelSwitchTasks')
);
const SalesChannelSwitchTaskDetail = React.lazy(
    () =>
        import(/* webpackPrefetch: true, webpackChunkName: "salesChannelSwitchTaskDetail" */ 'src/components/pages/salesChannels/salesChannelSwitchTaskDetail')
);

const Products = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "products" */ 'src/components/pages/products'));

const Tools = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "tools" */ 'src/components/pages/tools/index'));

const Settings = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "settings" */ 'src/components/pages/settings/index'));

const AI = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "ai" */ 'src/components/pages/ai'));

const ProfanityFilterMenu = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "profanityFilterMenu" */ 'src/components/pages/profanityFilter/profanityFilterMenu')
);
const ProfanityFilterRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "profanityFilterRules" */ 'src/components/pages/profanityFilter/profanityFilterRules')
);
const ProfanityFilterRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "profanityFilterRule" */ 'src/components/pages/profanityFilter/profanityFilterRule')
);
const PorfanityFilterAnalytics = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "profanityFilterAnalytics" */ 'src/components/pages/profanityFilter/profanityFilterAnalytics')
);

const MoqRule = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "moqRule" */ 'src/components/pages/moq/moq'));

const DAM = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "dam" */ 'src/components/pages/dam'));
const DamRelayer = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "dam" */ 'src/components/pages/damRelayer'));

const DashboardAnalytics = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "dasboardAnalytics" */ 'src/components/pages/dashboardAnalytics')
);

const ReportViewsTryOn = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "reportViewsTryOn" */ 'src/components/pages/reportViewsTryOn'));
const SymbolMapRules = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "symbolMapRules" */ 'src/components/pages/symbolMapRules'));
const SymbolMapRule = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "symbolMapRule" */ 'src/components/pages/symbolMapRule'));

const ProviderConfiguration = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "providerConfiguration" */ 'src/components/pages/integrationSettings/providerConfiguration')
);
const PrintfulProviderPage = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "providerConfiguration" */ 'src/components/pages/integrationSettings/providerConfiguration/printfulPage'
        )
);
const CustomCatProviderPage = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "providerConfiguration" */ 'src/components/pages/integrationSettings/providerConfiguration/customCatPage'
        )
);
const PrinteersProviderPage = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "providerConfiguration" */ 'src/components/pages/integrationSettings/providerConfiguration/printeersPage'
        )
);

const PlacementProduct = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "placement-product" */ 'src/components/pages/placementProduct'));
const Placements = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "placements" */ 'src/components/pages/placements'));
const Placement = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "placement" */ 'src/components/pages/placement'));

const InvoicesPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "invoices-page" */ 'src/components/pages/invoices'));

const Preview3DProducts = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "preview3DProducts" */ 'src/components/pages/preview3DProducts/preview3DProducts')
);
const Preview3DPage = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "preview3D" */ 'src/components/pages/preview3D'));
const CameraConfiguration = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "cameraConfiguration" */ 'src/components/pages/preview3D/cameraConfiguration')
);
const VirtualTryOn = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName:"virtualTryOn" */ 'src/components/pages/virtualTryOn'));

// Preset configurations
const PresetConfigurationsProducts = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "presetConfigurationsProducts" */ 'src/components/pages/presetConfigurations/products/presetConfigurationsProducts'
        )
);
const PresetConfigurationsProductPicked = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "presetConfigurationsProductPicked" */ 'src/components/pages/presetConfigurations/products/presetConfigurationsProductPicked'
        )
);

//Connectors
const ConnectorRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "connectorRules" */ 'src/components/pages/connectors/connectorRules/connectorRules')
);
const ConnectorRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "connectorRules" */ 'src/components/pages/connectors/connectorRules/connectorRule')
);
const EmptyPage = () => null;

const PermissionsLogin = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "permissionsLogin" */ 'src/components/pages/permissions/permissionsLogin')
);
const PermissionsLoginSellerSelection = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "permissionsLoginSellerSelection" */ 'src/components/pages/permissions/permissionsLoginSellerSelection'
        )
);
const PermissionsLoginSellerSelectionCompleted = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "permissionsLoginSellerSelectionCompleted" */ 'src/components/pages/permissions/permissionsLoginSellerSelectionCompleted'
        )
);

const CsvImporter = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "csvImporter" */ 'src/components/pages/tools/csvImporter'));
const AIImageGeneration = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "aiImageGeneration" */ 'src/components/pages/tools/aiImageGeneration')
);
const DesignWithAi = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "designWithAi" */ 'src/components/pages/tools/designWithAi'));
const AiStudio = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "aiStudio" */ 'src/components/pages/aiStudio'));
const AIProductStagingRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "aiProductStagingRules" */ 'src/components/pages/aiProductStagingRules/aiProductStagingRules')
);
const AIProductStagingRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "aiProductStagingRule" */ 'src/components/pages/aiProductStagingRules/aiProductStagingRule')
);
const AIVirtualModelRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "aiVirtualModelRules" */ 'src/components/pages/aiVirtualModelRules/aiVirtualModelRules')
);
const AIVirtualModelRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "aiVirtualModelRule" */ 'src/components/pages/aiVirtualModelRules/aiVirtualModelRule')
);
const CustomersDesigns = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "customersDesigns" */ 'src/components/pages/customersDesigns'));
const CustomerConfigurations = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "customerConfigurations" */ 'src/components/pages/compositionDrafts')
);
const RulesOrImages = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "rulesOrImages" */ 'src/components/pages/areaBackgroundMenu/rulesOrImages')
);
const AreaBackgroundRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "areaBackgroundRules" */ 'src/components/pages/areaBackgroundMenu/areaBackgroundRules')
);
const AreaBackgroundRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "areaBackgroundRule" */ 'src/components/pages/areaBackgroundMenu/areaBackgroundRule')
);

const SubscriptionChecker = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "subscriptionChecker" */ 'src/components/pages/subscriptions/checker')
);
const SubscriptionCheckForCancelling = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "subscriptionCheckForCancelling" */ 'src/components/pages/subscriptions/checkForCancelling')
);
const SubscriptionRequestTrialExtension = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "subscriptionRequestTrialExtension" */ 'src/components/pages/subscriptions/requestTrialExtension')
);
const SubscriptionCheckForExisting = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "subscriptionCheckForExisting" */ 'src/components/pages/subscriptions/checkForExisting')
);
const SubscriptionCheckForActivation = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "subscriptionCheckForActivation" */ 'src/components/pages/subscriptions/checkForActivation')
);

const ECommerceConnections = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "eCommerceConnections" */ 'src/components/pages/ecommerceConnection/eCommerceConnection')
);
const PantoneColors = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "pantoneColors" */ 'src/components/pages/pantoneColors'));

// Temporary until we migrate all the backoffice
const isOldProductView = Boolean(document.getElementById('product-tab-content'));

const BackgroundColorRules = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "backgroundRecolorRules" */ 'src/components/pages/backgroundColor/backgroundColorRules')
);
const BackgroundColorRule = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "backgroundRecolorRule" */ 'src/components/pages/backgroundColor/backgroundColorRule')
);

const UserSyncChecker = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "userSyncChecker" */ 'src/components/pages/userSyncChecker/userSyncChecker')
);

const PrintfulIntegration = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "printfulActivation" */ 'src/components/pages/integrationSettings/providerConfiguration/providers/printfulIntegration'
        )
);

const AmazonChoiceMarketplacePage = React.lazy(
    () => import(/* webpackPrefetch: true, webpackChunkName: "amazonChoiceMarketplacePage" */ 'src/components/pages/amazonChoiceMarketplace')
);

const RedirectForUpgradePlanForShopify = React.lazy(
    () =>
        import(
            /* webpackPrefetch: true, webpackChunkName: "RedirectForUpgradePlanForShopifyProps" */ 'src/components/pages/choosePlan/redirectForUpgradePlanForShopify'
        )
);

const RedirectPartner = React.lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "RedirectPartner" */ 'src/components/pages/partnerProgram'));

type Route = {
    url: string;
    component: React.ComponentType;
    isHeaderHidden?: boolean;
    isMenuHidden?: boolean;
    isSubscriptionRequested?: boolean;
    forceTools?: boolean;
    isGuestAllowed?: boolean;
};
const routes: Route[] = [
    { url: backofficeUrl(''), component: Bootstrap },
    { url: backofficeUrl('/'), component: Bootstrap },
    { url: backofficeUrl('/dashboard'), component: Dashboard, isSubscriptionRequested: true },
    { url: backofficeUrl('/internal/e-commerce'), component: ECommerceConnections, isSubscriptionRequested: true },

    { url: backofficeUrl('/e-commerce'), component: ECommerceRedirect, isSubscriptionRequested: true },
    { url: backofficeUrl('/amazonSelectMarketplace'), component: AmazonChoiceMarketplacePage },
    { url: backofficeUrl('/choose-ecommerce'), component: ECommerceRedirect, isSubscriptionRequested: true },
    { url: backofficeUrl('/connect-shop'), component: PlatformSelection, isSubscriptionRequested: true },

    { url: backofficeUrl('/e-commerce/start'), component: EcommerceStart, forceTools: true, isMenuHidden: true, isSubscriptionRequested: true },
    { url: backofficeUrl('/seller/subscription'), component: ChoosePlan, forceTools: true, isMenuHidden: true, isSubscriptionRequested: false },
    { url: backofficeUrl('/sellersubscriptionstart'), component: SetDefaultPlan, forceTools: true, isMenuHidden: true, isSubscriptionRequested: false },
    {
        url: backofficeUrl('/subscriptionstartshopify'),
        component: StartsShopifySubscription,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false
    },

    { url: backofficeUrl('/draft-designs'), component: CustomersDesigns, isSubscriptionRequested: true },
    { url: backofficeUrl('/draft-configurations'), component: CustomerConfigurations, isSubscriptionRequested: true },
    { url: backofficeUrl('/products'), component: isOldProductView ? EmptyPage : Products, isSubscriptionRequested: true },
    {
        url: backofficeUrl('/product/:page?/:subpage?/:subpage2?'),
        component: isOldProductView ? EmptyPage : ProductConfiguration,
        isSubscriptionRequested: true
    },

    { url: backofficeUrl('/test-mode/:productId'), component: TestPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/test-mode/'), component: TestPage, isSubscriptionRequested: true },

    { url: backofficeUrl('/3Dpreview-and-AR/products'), component: Preview3DProducts, isSubscriptionRequested: true },
    { url: backofficeUrl('/3Dpreview-and-AR/cameras'), component: CameraConfiguration, isSubscriptionRequested: true },
    { url: backofficeUrl('/3Dpreview-and-AR/:subpage?'), component: Preview3DPage, isSubscriptionRequested: true },

    // Preset configurations
    { url: backofficeUrl('/preset-configurations'), component: PresetConfigurationsProducts, isSubscriptionRequested: true },
    { url: backofficeUrl('/preset-configurations/products'), component: PresetConfigurationsProducts, isSubscriptionRequested: true },
    { url: backofficeUrl('/preset-configurations/products/:id'), component: PresetConfigurationsProductPicked, isSubscriptionRequested: true },

    { url: backofficeUrl('/pricing-rule/:subpage?'), component: Pricing, isSubscriptionRequested: true },
    { url: backofficeUrl('/pricing-rule-embedded/:subpage?'), component: Pricing, isMenuHidden: true, isHeaderHidden: true, isSubscriptionRequested: true },

    { url: backofficeUrl('/pricings'), component: PricingsType, isSubscriptionRequested: true },
    { url: backofficeUrl('/pricings-base'), component: PricingsBase, isSubscriptionRequested: true },
    { url: backofficeUrl('/pricings-rules'), component: PricingsAdvanced, isSubscriptionRequested: true },

    { url: backofficeUrl('/printing-methods'), component: PrintTypes, isSubscriptionRequested: true },
    { url: backofficeUrl('/printing-method/:id'), component: PrintTypePage, isSubscriptionRequested: true },
    { url: backofficeUrl('/printing-method/'), component: PrintTypePage, isSubscriptionRequested: true },
    { url: backofficeUrl('/printing-method-embedded/:id'), component: PrintTypePage, isMenuHidden: true, isHeaderHidden: true, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/extra-options'), component: AdditionalAttributes, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/extra-option/:id'), component: AdditionalAttributePage, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/extra-option/'), component: AdditionalAttributePage, isSubscriptionRequested: true },

    { url: backofficeUrl('/order/:id'), component: OrderPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/order/'), component: OrderPage, isSubscriptionRequested: true },
    // @ts-ignore
    { url: backofficeUrl('/order/code'), component: ShopifyOrderDetailContent, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/orders'), component: OrdersSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/orders/'), component: Orders, isSubscriptionRequested: true },
    { url: backofficeUrl('/marketplace-order/:id'), component: MarketplaceOrderPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/marketplace-order/'), component: MarketplaceOrderPage, isSubscriptionRequested: true },

    { url: backofficeUrl('/orders/place/'), component: OfflineOrder, isSubscriptionRequested: true },
    { url: backofficeUrl('/orders/create/'), component: CreateOfflineOrder, isSubscriptionRequested: true },

    { url: backofficeUrl('/order/design/edit/:designId'), component: OrderDesignEditor, isSubscriptionRequested: true },
    { url: backofficeUrl('/order/design/edit/'), component: OrderDesignEditor, isSubscriptionRequested: true },

    { url: backofficeUrl('/marketplace-orders/:id'), component: MarketplaceOrder, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/bulk-variation-form/products'), component: BulkProductsList, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/bulk-variation-form/:modelId'), component: BulkVariationPage, isSubscriptionRequested: true },

    {
        url: backofficeUrl('/seller/checkoutforplan/:planIdForNewCustomer'),
        component: UserCheckout,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false
    },
    { url: backofficeUrl('/seller/checkoutforplan/'), component: UserCheckout, isSubscriptionRequested: false },

    { url: backofficeUrl('/user/:tab?'), component: UserPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/seller/overviewsubscription'), component: UserPage, isSubscriptionRequested: false },

    { url: '/:culture/support', component: Support, forceTools: true, isMenuHidden: true, isGuestAllowed: true, isSubscriptionRequested: false },

    { url: backofficeUrl('/seller/billings'), component: InvoicesPage, isSubscriptionRequested: false },
    //{ url: backofficeUrl('/seller/billings/details/:id'), component: Invoice },

    { url: backofficeUrl('/seller/upgradetrialsubscription'), component: UserCheckout, forceTools: true, isMenuHidden: true, isSubscriptionRequested: false },

    { url: backofficeUrl('/settings/custom-print-file-names'), component: CustomPrintFileNames, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/shopifyNeedUpdateScope'), component: ShopifyNeedUpdateScope, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/integration-settings/:platform'), component: PlatformIntegrationSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/integration-settings'), component: PlatformIntegrationSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/general'), component: GeneralSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/general-configurator'), component: GeneralSettingsConfigurator, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/additional-properties'), component: ExtensionFieldsSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/additional-properties/:id'), component: ExtensionFieldPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/additional-property'), component: ExtensionFieldPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/pdf-preview'), component: PdfPreviewSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/pdf-summary'), component: PdfSummarySettings, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/standalone'), component: StandaloneSites, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/standalone/:siteId/collection/new'), component: StandaloneSiteCollectionDetail, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/standalone/:siteId/collection/:collectionId'), component: StandaloneSiteCollectionDetail, isSubscriptionRequested: true },
    {
        url: backofficeUrl('/settings/standalone/:siteId/theme/:themeId/settings'),
        component: ThemeSettingsEditor,
        isMenuHidden: true,
        isHeaderHidden: true,
        isSubscriptionRequested: true
    },
    { url: backofficeUrl('/settings/standalone/:id/:page?'), component: StandaloneSiteDetail, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/color-palette-settings'), component: ColorPaletteSettings, isSubscriptionRequested: true },
    {
        url: backofficeUrl('/settings/color-palette-settings/palette/'),
        component: PaletteColorsPage, // Parent component that manages both views
        isSubscriptionRequested: true
    },
    { url: backofficeUrl('/settings/color-palette-settings/palette/:id'), component: PaletteColorsPage, isSubscriptionRequested: true },

    {
        url: backofficeUrl('/settings/color-palette-settings/palette/'),
        component: PantoneLibraryPage, // Parent component that manages both views
        isSubscriptionRequested: true
    },
    { url: backofficeUrl('/settings/color-palette-settings/palette/:id'), component: PantoneLibraryPage, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/shapes/:subpage'), component: Shapes, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/shapes/'), component: Shapes, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/image-gallery/:subpage'), component: Images, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/image-gallery'), component: Images, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/template-product-selection'), component: ProductTemplates, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/template-product-selection/templates/:productId'), component: ProductItemTemplates, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/template-product-selection/tasks/:taskId'), component: TemplateTaskDetail, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/customizer-theme-items'), component: CustomizerThemeItems, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/customizer-theme-item/:category'), component: CustomizerThemeItemPage, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/messages-and-alerts/'), component: SettingsMessages, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/customizer-message/:eventId'), component: SettingsMessage, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/customizerMessagesByEvent/:eventId'), component: CustomizerMessagesByEvent, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/customizerMessageByEvent/:eventId/:messageId'), component: CustomizerMessageByEvent, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/translations'), component: Translations, isSubscriptionRequested: true },

    { url: backofficeUrl('/integration/hotjar'), component: Hotjar, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/googleTagManager'), component: GoogleTagManager, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/zapier'), component: Zapier, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/provider-configuration-page'), component: ProviderConfiguration, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/provider-configuration-page/printful'), component: PrintfulProviderPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/provider-configuration-page/customcat'), component: CustomCatProviderPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/provider-configuration-page/printeers'), component: PrinteersProviderPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/provider-configuration-page/:subpage'), component: ProviderConfiguration, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/webhooks'), component: WebHooksSettings, isSubscriptionRequested: true },

    { url: backofficeUrl('/landings/customizer'), component: CustomizerLanding, forceTools: true, isMenuHidden: true, isSubscriptionRequested: true },
    { url: backofficeUrl('/landings/configurator'), component: ConfiguratorLanding, forceTools: true, isMenuHidden: true, isSubscriptionRequested: true },
    { url: backofficeUrl('/landings/viewer'), component: ViewerLanding, forceTools: true, isMenuHidden: true, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings'), component: Settings, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools'), component: AI, isSubscriptionRequested: true },
    { url: backofficeUrl('/sales-channels/switch-tasks/:taskId'), component: SalesChannelSwitchTaskDetail, isSubscriptionRequested: true },
    { url: backofficeUrl('/sales-channels/switch-tasks'), component: SalesChannelSwitchTasks, isSubscriptionRequested: true },
    { url: backofficeUrl('/sales-channels'), component: SalesChannels, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools'), component: Tools, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/fill-background'), component: RulesOrImages, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/fill-background/rules'), component: AreaBackgroundRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/fill-background/rule/:id?'), component: AreaBackgroundRule, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/fonts'), component: Fonts, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/profanity-filter-menu'), component: ProfanityFilterMenu, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/profanity-filter-and-image-moderator'), component: ProfanityFilterRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/profanity-filter-rule'), component: ProfanityFilterRule, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/profanity-filter-analytics'), component: PorfanityFilterAnalytics, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/moq'), component: MoqRule, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/moq-rule/:id?'), component: MoqRule, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/color-system'), component: ColorSystem, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/color-system/palette/:id?'), component: ColorSystemPage, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/collage-layouts'), component: CollageLayout, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/collage-layouts/:tab'), component: CollageLayout, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/collage-layouts/rules'), component: GridRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/collage-layouts/rule/:id?'), component: GridRule, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/get-a-quote'), component: GetAQuote, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/get-a-quote/quote-rules'), component: QuoteRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/get-a-quote/quote-rule/:id?'), component: QuoteRule, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/get-a-quote/quotes'), component: Quotes, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/get-a-quote/quote/:id?'), component: Quote, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/get-a-quote/quote-email-notifications'), component: QuoteNotificationSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/get-a-quote/quote-summary'), component: QuoteSummarySettings, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/symbol-map-rules'), component: SymbolMapRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/symbol-map-rule/:id?'), component: SymbolMapRule, isSubscriptionRequested: true },

    { url: backofficeUrl('/report-views-try-on'), component: ReportViewsTryOn, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/design-to-template/:designId/:page'), component: DesignToTemplate, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/advanced-print-file/products'), component: PlacementProduct, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/advanced-print-file/:modelId'), component: Placements, isSubscriptionRequested: true },
    // @ts-ignore
    { url: backofficeUrl('/tools/placement-product/:modelId/:placementId?'), component: Placement, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/connector/:connectorKey/connector-rules'), component: ConnectorRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/connector/:connectorKey/connector-rule/:ruleId?'), component: ConnectorRule, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/CSVImporter'), component: CsvImporter, isSubscriptionRequested: true },

    { url: backofficeUrl('/ai-creative-tools/image-generation-editing'), component: AIImageGeneration, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools/design-generation'), component: DesignWithAi, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools/studio'), component: AiStudio, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools/lifestyle-scenes-rules'), component: AIProductStagingRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools/lifestyle-scenes-builder/:id?'), component: AIProductStagingRule, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools/ai-tryon-rules'), component: AIVirtualModelRules, isSubscriptionRequested: true },
    { url: backofficeUrl('/ai-creative-tools/ai-tryon-builder/:id?'), component: AIVirtualModelRule, isSubscriptionRequested: true },

    { url: backofficeUrl('/dam'), component: DAM, isSubscriptionRequested: true },
    { url: backofficeUrl('/damRelayer'), component: DamRelayer, isHeaderHidden: true, isMenuHidden: true, isSubscriptionRequested: true },

    { url: backofficeUrl('/dashboardAnalytics'), component: DashboardAnalytics, isSubscriptionRequested: true },

    { url: backofficeUrl('/virtual-try-on/:subpage?'), component: VirtualTryOn, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/theme'), component: InterfaceEditor, isSubscriptionRequested: true },
    {
        url: backofficeUrl('/settings/UserInterface/:sellerUserInterfaceId'),
        component: UserInterfaceSettingsEditor,
        isMenuHidden: true,
        isHeaderHidden: true,
        isSubscriptionRequested: true
    },
    { url: backofficeUrl('/settings/UserInterface/new'), component: UserInterfaceNew, isMenuHidden: true, isHeaderHidden: true, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/UserInterface'), component: UserInterfaceSettings, isSubscriptionRequested: true },
    { url: backofficeUrl('/settings/Theme-3D-Configurator'), component: UserInterfaceSettingsConfigurator, isSubscriptionRequested: true },

    { url: backofficeUrl('/settings/Seller/PantoneColors'), component: PantoneColors, isSubscriptionRequested: true },

    { url: backofficeUrl('/tools/background-recolor/rule/:id?'), component: BackgroundColorRule, isSubscriptionRequested: true },
    { url: backofficeUrl('/tools/background-recolor'), component: BackgroundColorRules, isSubscriptionRequested: true },
    // old URL redirect to /tools/background-recolor
    {
        url: backofficeUrl('/Tools/BackgroundRecolorRules'),
        component: BackgroundRecolorRedirect,
        isSubscriptionRequested: true
    },

    /*Guest allowed routes*/
    { url: '/:culture/pricing', component: ChoosePlan, forceTools: true, isMenuHidden: true, isGuestAllowed: true, isSubscriptionRequested: false },
    { url: '/:culture/pricing/:ecommercename', component: ChoosePlan, isGuestAllowed: true, isSubscriptionRequested: false },

    { url: '/:culture/pricingV2', component: ChoosePlanV2, forceTools: true, isMenuHidden: true, isGuestAllowed: true, isSubscriptionRequested: false },
    { url: '/:culture/pricingV2/:ecommercename', component: ChoosePlanV2, isGuestAllowed: true, isSubscriptionRequested: false },

    { url: backofficeUrl('/seller/redirectForUpgradePlanForShopify'), component: RedirectForUpgradePlanForShopify },

    { url: '/:culture/PartnerProgramContact', component: RedirectPartner, isGuestAllowed: true, isSubscriptionRequested: false },

    /* Checker pages */
    {
        url: backofficeUrl('/user_sync_checker'),
        component: UserSyncChecker,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false,
        isGuestAllowed: true
    },
    {
        url: backofficeUrl('/SubscriptionChecker'),
        component: SubscriptionChecker,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false,
        isGuestAllowed: true
    },
    {
        url: backofficeUrl('/Subscription/CheckForActivation'),
        component: SubscriptionCheckForActivation,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false,
        isGuestAllowed: true
    },
    {
        url: backofficeUrl('/Subscription/CheckForExisting'),
        component: SubscriptionCheckForExisting,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false,
        isGuestAllowed: true
    },
    {
        url: backofficeUrl('/Subscription/CheckForCancelling'),
        component: SubscriptionCheckForCancelling,
        forceTools: true,
        isMenuHidden: true,
        isSubscriptionRequested: false,
        isGuestAllowed: true
    },
    {
        url: backofficeUrl('/Subscription/TrialExtension'),
        component: SubscriptionRequestTrialExtension,
        isHeaderHidden: true,
        isMenuHidden: true,
        isSubscriptionRequested: false
    },
    { url: backofficeUrl('/cli-login'), component: CliLogin, isHeaderHidden: true, isMenuHidden: true, isSubscriptionRequested: false },

    { url: backofficeUrl('/QuotesNoActivated'), component: EmptyPage, isSubscriptionRequested: true },
    { url: backofficeUrl('/integration/Printful/Auth'), component: PrintfulIntegration, forceTools: false, isMenuHidden: true, isSubscriptionRequested: true }
];

export default routes;
